// URL slugs, derived from the same MODULES list the dashboard and permission
// screen use — so a module can never appear in the grid without a route.

import { MODULES, MODULE_BY_ID, SETTINGS_TILE } from './utils/constants';
import type { ModuleId, ModuleRoute } from './utils/constants';

export const SETTINGS_ROUTE = SETTINGS_TILE.route;
export const SETTINGS_MODULE_ID = SETTINGS_TILE.id;

/**
 * The Settings screen's left-menu sections, in menu order. The active one is
 * the second URL segment: /settings/lists. Keys are also what SettingsPage
 * switches on, so adding a section means adding it here and there.
 */
export const SETTINGS_SECTIONS = [
  { key: 'identity', icon: '⛪', label: 'Church Identity', hint: 'Name, address, contact, logo' },
  { key: 'appearance', icon: '🎨', label: 'Appearance', hint: 'Brand colour, theme, display size' },
  { key: 'modules', icon: '🧩', label: 'Modules & Dashboard', hint: 'Show, hide and rename tiles' },
  { key: 'lists', icon: '📋', label: 'Dropdown Lists', hint: 'Event types, designations, categories…' },
  { key: 'rules', icon: '⚖️', label: 'Rules & Formats', hint: 'Dates, currency, page size, sacraments' },
  { key: 'templates', icon: '🖨️', label: 'Print Templates', hint: 'Layouts for certificates, records and reports' },
  { key: 'language', icon: '🌐', label: 'Language', hint: 'Default language at sign-in' },
  { key: 'registration', icon: '📝', label: 'Public Registration', hint: 'Family self-registration link' },
  { key: 'users', icon: '👥', label: 'Users & Permissions', hint: 'Accounts and module access' },
  { key: 'approvals', icon: '✅', label: 'Approvals', hint: 'Approval levels and who approves' }
] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]['key'];
export const DEFAULT_SETTINGS_SECTION: SettingsSection = 'identity';

export const isSettingsSection = (value?: string | null): value is SettingsSection =>
  SETTINGS_SECTIONS.some((s) => s.key === value);

export const settingsPath = (section?: SettingsSection): string =>
  section && section !== DEFAULT_SETTINGS_SECTION
    ? `/${SETTINGS_ROUTE}/${section}`
    : `/${SETTINGS_ROUTE}`;

// route slug -> module id
/** Settings has no permission key, so it is not in MODULES. */
export type RoutableModuleId = ModuleId | typeof SETTINGS_MODULE_ID;
export type RoutableModuleRoute = ModuleRoute | typeof SETTINGS_ROUTE;

export const MODULE_ID_BY_ROUTE: Record<string, RoutableModuleId> = MODULES.reduce(
  (acc, m) => ({ ...acc, [m.route]: m.id }),
  {
    [SETTINGS_ROUTE]: SETTINGS_MODULE_ID,
    // Church Configuration merged into Settings. The old slug keeps bookmarks
    // and stored last-visited resolving.
    'church-config': SETTINGS_MODULE_ID,
    // Families and Members merged into one module ('congregation'). These legacy
    // slugs keep old bookmarks and stored last-visited resolving; Congregation
    // reads the slug to open the matching tab.
    families: 'Members & Families',
    members: 'Members & Families',
    // Donations and Collections merged into one module ('offerings'). These
    // legacy slugs keep old bookmarks and stored last-visited resolving.
    donations: 'Offerings',
    collections: 'Offerings',
    // Marriage banns + certificates merged into 'marriage'; child baptism +
    // confirmation merged into 'baptism'. Old slugs still resolve.
    'marriage-banns': 'Marriage',
    'marriage-certificates': 'Marriage',
    'child-baptism': 'Baptism',
    confirmation: 'Baptism'
  } as Record<string, RoutableModuleId>
);

export const routeForModuleId = (moduleId: string): string | undefined =>
  moduleId === SETTINGS_MODULE_ID ? SETTINGS_ROUTE : MODULE_BY_ID[moduleId]?.route;

export const pathForModuleId = (moduleId: string): string => {
  const route = routeForModuleId(moduleId);
  return route ? `/${route}` : '/';
};

export const moduleIdForRoute = (route?: string): RoutableModuleId | undefined =>
  (route ? MODULE_ID_BY_ROUTE[route] : undefined);
